import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_BASE ?? '';

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem('manage_token'));
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(Boolean(localStorage.getItem('manage_token')));
    const [maintenance, setMaintenance] = useState(false);

    const logout = useCallback(() => {
        localStorage.removeItem('manage_token');
        setToken(null);
        setUser(null);
        setMaintenance(false);
    }, []);

    // โหลด user info เมื่อมี token
    useEffect(() => {
        if (!token) {
            setAuthLoading(false);
            return;
        }
        let cancelled = false;
        fetch(`${API_BASE}/api/auth/me`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => { if (!cancelled) setUser(data); })
            .catch(() => { if (!cancelled) logout(); })
            .finally(() => { if (!cancelled) setAuthLoading(false); });

        return () => { cancelled = true; };
    }, [token, logout]);

    // ตรวจสถานะ maintenance หลัง user โหลดสำเร็จ (เฉพาะ system_operation เท่านั้น)
    useEffect(() => {
        if (!token || !user || user.role !== 'system_operation') return;
        fetch(`${API_BASE}/api/system/maintenance`, {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => { if (data != null) setMaintenance(Boolean(data.maintenance_mode)); })
            .catch(() => {});
    }, [user?.id, token]);

    const login = (newToken) => {
        localStorage.setItem('manage_token', newToken);
        setToken(newToken);
    };

    const refreshUser = useCallback(() => {
        if (!token) return;
        fetch(`${API_BASE}/api/auth/me`, {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => setUser(data))
            .catch(() => logout());
    }, [token, logout]);

    // fetch พร้อม Bearer token — ตรวจ 503 สำหรับ maintenance
    const authFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(`${API_BASE}${url}`, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
            if (res.status === 503) {
                setMaintenance(true);
            }
            return res;
        },
        [token]
    );

    return (
        <AuthContext.Provider
            value={{
                token,
                user,
                role: user?.role ?? null,
                isAuthenticated: Boolean(token),
                authLoading,
                maintenance,
                setMaintenance,
                login,
                logout,
                authFetch,
                refreshUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
